import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { withRouter } from 'react-router-dom';
import {
  Row,
  Col,
  Table,
  Tag,
  Icon,
} from 'antd';
import {
  PAGES,
  ROLES,
  LYNX_CONNECT,
} from 'appconstants';
import RBAC from 'components/rbac';
import queryString from 'querystring';
import { SpButton } from 'components/DesignKit';
import debounce from 'lodash.debounce';
import moment from 'moment-timezone';
import AddNewInvoiceModal from './addNewInvoice';
import DisplayUploadGuildelines from './displayUploadGuidelines';
import DisplayUploadSuccess from './displayUploadSuccess';
import DisplayImportantMessage from './displayImportantMessage';

type Props = {
  // loading: boolean,
  test: boolean,
  // validator: Boolean,
  location: {
    pathname: string,
    search: string,
  },
  history: {
    push: Function,
  };
  selectedAccount: Object,
  // submitting: Boolean,
  lynxConnectInvoices: Array,
  lynxConnectInvoicesTotalElements: Number,
  lynxConnectVendor: Array,
  lynxConnectLocations: Array,
  lynxConnectAddNewInvoiceStatus: String,
  fetchInvoice: Function,
  fetchInvoiceMeta: Function,
  addNewInvoice: Function,
  resetAddNewInvoiceStatus: Function,
  resetInvoices: Function,
  lynxConnectLoading: Boolean,
  setAuditEvent: Function,
};

const Invoices = (props: Props) => {
  const {
    // loading,
    // validator,
    selectedAccount,
    // submitting,
    test,
    history,
    location,
    lynxConnectInvoices,
    lynxConnectInvoicesTotalElements,
    lynxConnectVendor,
    lynxConnectLocations,
    lynxConnectAddNewInvoiceStatus,
    fetchInvoice,
    fetchInvoiceMeta,
    addNewInvoice,
    resetAddNewInvoiceStatus,
    resetInvoices,
    lynxConnectLoading,
    setAuditEvent,
  } = props;

  // reset the invoices state
  useEffect(() => {
    resetInvoices({});
  }, []);

  const [pagination, setPagination] = useState({});
  const [uploadGuidelineModalVisibility, setUploadGuidelineModalVisibility] = useState(false);
  const [addFileModalVisibility, setAddFileModalVisibility] = useState(false);
  const [uploadSuccessModalVisibility, setUploadSuccessModalVisibility] = useState(false);
  const [confirmInvoiceLineItemModalVisibility, setConfirmInvoiceLineItemModalVisibility] = useState(false);
  const [showInvoiceDetailGid, setShowInvoiceDetailGid] = useState(null);
  const [currentSelectInvoiceStatus, setCurrentSelectInvoiceStatus] = useState(null);

  // use actual api to save new location data
  const debouncedAddNewLynxConnectInvoice = useMemo(() => (
    debounce(addNewInvoice, 1000, { leading: true })
  ), [addNewInvoice]);

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const {
      pageNum = 1,
      sortBy = 'createdAt',
      direction = 'DESC',
    } = parsed;
    const filterByStatus = parsed['status.IN'];
    fetchInvoice({
      filterByStatus,
      pageNum,
      sortBy,
      direction,
    });
    fetchInvoiceMeta({});
  };

  useEffect(() => {
    if (location.search.includes('status.IN')) {
      getData(location.search);
    }
  }, [location.search, test, selectedAccount]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: lynxConnectInvoicesTotalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [
    location.search,
    lynxConnectInvoices,
    lynxConnectInvoicesTotalElements,
  ]);

  const handleTableChange = (page, filters, sorter) => {
    const parsed = queryString.parse(location.search);
    const filterByStatus = parsed['status.IN'] ?? '';
    const selectPageNumber = page.current;
    let params = `&pageNum=${selectPageNumber}`;
    if (filterByStatus) {
      params = `&status.IN=${filterByStatus}${params}`;
    }

    if (sorter && sorter.order) {
      const sortDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
      params += `&sortBy=${sorter.field}&direction=${sortDirection}`;
    }

    history.push({
      pathname: PAGES.LYNX_CONNECT_INVOICES,
      search: params,
    });
  };

  const handleReadyToProcess = (status, invoiceDetail) => {
    if (status !== 'Ready to Process' && status !== 'Waiting For Approval') {
      return;
    }
    setCurrentSelectInvoiceStatus(status);
    setConfirmInvoiceLineItemModalVisibility(true);
    setShowInvoiceDetailGid(invoiceDetail.gid);
  };

  const handleActivityLog = (invoiceGid) => {
    history.push(`/lynxconnect/activity-log/${invoiceGid}`);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '30%',
      align: 'left',
      render: (text) => (
        <span style={{ cursor: 'pointer' }}>
          {text || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      width: '10%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text) => (
        <span style={{ cursor: 'pointer' }}>
          {text || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'Filename',
      dataIndex: 'fileName',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text) => (
        <span style={{ cursor: 'pointer' }}>
          {text || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: 'Scan Date',
      dataIndex: 'scanDate',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (text) => {
        let tzDate = text;
        if (selectedAccount && selectedAccount.timezone) {
          tzDate = moment
            .utc(text)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
        }
        return (
          <span style={{ cursor: 'pointer' }}>
            {tzDate || <>&#8211;</>}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '15%',
      align: 'left',
      render: (text, record) => (
        <Tag
          style={{
            cursor: 'pointer',
            backgroundColor: LYNX_CONNECT.TEXT_COLOR.INVOICE_STATUS[text],
            color: 'black',
          }}
          color={LYNX_CONNECT.TEXT_COLOR.INVOICE_STATUS[text] || 'blue'}
          onClick={() => handleReadyToProcess(text, record)}
        >
          {text || <>&#8211;</>}
        </Tag>
      ),
    },
    {
      title: 'Activity Log',
      width: '15%',
      align: 'center',
      render: (text, record) => (
        <Icon
          type="calendar"
          onClick={() => handleActivityLog(record.gid)}
          className="p-3 btn hover-border"
          style={{
            fontSize: '20px',
          }}
        />
      ),
    },
  ];

  const handleTableRowClick = (record) => ({
    onClick: () => handleReadyToProcess(record.status, record),
  });

  return (
    <div>
      <RBAC permit={
        [ROLES.ADMINISTRATOR,
          ROLES.OWNER,
          ROLES.DEVELOPER,
          ROLES.OPERATIONS,
          // ROLES.ANALYST,
          ROLES.SUPPORT,
          ROLES.INTERNALSUPPORT,
          ROLES.POS,
          ROLES.SUPERUSER,
          ROLES.ONBOARDING,
          ROLES.OFFICEMANAGER,
          ROLES.OFFICESUPPORT,
          ROLES.WAREHOUSE_ASSOCIATE,
          ROLES.PRICING_ANALYST,
        ]
            }
      >
        <Row type="flex" justify="end">
          <Col>
            <SpButton
              type="secondary"
              shape="round"
              className="mr-3 mt-3"
              ghost
              onClick={() => setUploadGuidelineModalVisibility(true)}
            >
              {'\u002B'}&nbsp;New Scan
            </SpButton>
          </Col>
        </Row>
        <Row>
          <Table
            columns={columns}
            rowKey={(record) => record.gid}
            dataSource={lynxConnectInvoices}
            pagination={pagination}
            rowClassName={(record) => (record.status === LYNX_CONNECT.INVOICE_STATUS_FORMATTED.READY_TO_PROCESS
              || record.status === LYNX_CONNECT.INVOICE_STATUS_FORMATTED.WAITING_FOR_APPROVAL ? 'cursor-pointer' : '')}
            onChange={handleTableChange}
            onRow={handleTableRowClick}
            style={{ margin: '20px' }}
          />
        </Row>
        <Row>
          {/* multi step modal flow */}
          <DisplayUploadGuildelines
            uploadGuidelineModalVisibility={uploadGuidelineModalVisibility}
            setUploadGuidelineModalVisibility={setUploadGuidelineModalVisibility}
            setAddFileModalVisibility={setAddFileModalVisibility}
          />
          <AddNewInvoiceModal
            lynxConnectVendor={lynxConnectVendor}
            lynxConnectLocations={lynxConnectLocations}
            addFileModalVisibility={addFileModalVisibility}
            lynxConnectAddNewInvoiceStatus={lynxConnectAddNewInvoiceStatus}
            setAddFileModalVisibility={setAddFileModalVisibility}
            debouncedAddNewLynxConnectInvoice={debouncedAddNewLynxConnectInvoice}
            resetAddNewInvoiceStatus={resetAddNewInvoiceStatus}
            setUploadSuccessModalVisibility={setUploadSuccessModalVisibility}
            lynxConnectLoading={lynxConnectLoading}
          />
          <DisplayUploadSuccess
            uploadSuccessModalVisibility={uploadSuccessModalVisibility}
            setUploadSuccessModalVisibility={setUploadSuccessModalVisibility}
          />
          <DisplayImportantMessage
            confirmInvoiceLineItemModalVisibility={confirmInvoiceLineItemModalVisibility}
            setConfirmInvoiceLineItemModalVisibility={setConfirmInvoiceLineItemModalVisibility}
            showInvoiceDetailGid={showInvoiceDetailGid}
            history={history}
            setAuditEvent={setAuditEvent}
            status={currentSelectInvoiceStatus}
            setStatus={setCurrentSelectInvoiceStatus}
          />
        </Row>
      </RBAC>
    </div>
  );
};

export default withRouter(Invoices);
